import { makeStyles, tokens } from '@coreai/fluentui-react';
import { TOOLTIP_HOVER_DELAY_MS } from './constants';

export const useStyles = makeStyles({
  /**
   * Root: absolutely positioned, full panel height, 40px wide invisible hit target.
   * Positioned OUTSIDE the panel edge by half its width so the center of the
   * hit area aligns with the seam.
   */
  root: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'col-resize',
    // Sit above panel content but below tooltips/menus
    zIndex: 11,
    // No native focus ring — we paint our own on the inner frame
    outline: 'none',
    // Let pill and tooltip overflow for the floating pill tooltip
    overflow: 'visible',
    // Touch: prevent scroll while grabbing
    touchAction: 'none',
    userSelect: 'none',
    // Subtle background on focus-visible for keyboard users
    '@media (prefers-reduced-motion: no-preference)': {
      transitionProperty: 'background-color',
      transitionDuration: tokens.durationNormal,
      transitionTimingFunction: tokens.curveEasyEase,
    },
  },
  rootLeftEdge: {
    left: '-20.25px',
  },
  rootRightEdge: {
    right: '-20px',
  },
  /** Keyboard focus ring — inset, accent color. */
  focusRing: {
    ':focus-visible::after': {
      content: '""',
      position: 'absolute',
      top: '8px',
      bottom: '8px',
      left: '14px',
      right: '14px',
      borderRadius: tokens.borderRadiusSmall,
      border: `2px solid ${tokens.colorStrokeFocus2}`,
      pointerEvents: 'none',
    },
  },
  /** The always-visible vertical pill. */
  pill: {
    width: '4px',
    height: '28px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralStroke1,
    opacity: 0.55,
    pointerEvents: 'none',
    transformOrigin: 'center',
    willChange: 'transform, opacity',
    transitionProperty: 'height, background-color, opacity, transform',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (forced-colors: active)': {
      backgroundColor: 'CanvasText',
      opacity: 1,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '1ms',
    },
  },
  pillHover: {
    height: '44px',
    backgroundColor: tokens.colorNeutralForeground1,
    opacity: 1,
  },
  pillActive: {
    height: '52px',
    backgroundColor: tokens.colorBrandStroke1,
    opacity: 1,
    transform: 'scaleX(1.4)',
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
    },
  },
  pillHidden: {
    opacity: 0,
    transform: 'scaleX(0.8)',
  },
  /** Dark floating tooltip pill. */
  tooltip: {
    position: 'absolute',
    top: '50%',
    // Default (left-edge handle on a right-side panel): tooltip floats LEFT
    // of the handle, out over the canvas.
    right: 'calc(100% + 4px)',
    transform: 'translateY(-50%) translateX(-4px) scale(0.96)',
    backgroundColor: tokens.colorNeutralBackgroundInverted,
    color: tokens.colorNeutralForegroundInverted,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    lineHeight: tokens.lineHeightBase200,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalSNudge}`,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusCircular,
    boxShadow: tokens.shadow8,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    opacity: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    zIndex: 12,
    transitionProperty: 'opacity, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    // 400ms delay only on the "appear" transition. Dismiss is instant.
    transitionDelay: '0ms',
    '@media (forced-colors: active)': {
      backgroundColor: 'CanvasText',
      color: 'Canvas',
      border: '1px solid CanvasText',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '1ms',
      transitionDelay: '0ms',
    },
  },
  tooltipRightEdge: {
    // Mirror for a right-edge handle (tooltip on the right)
    right: 'auto',
    left: 'calc(100% + 4px)',
    transform: 'translateY(-50%) translateX(4px) scale(0.96)',
  },
  tooltipVisible: {
    opacity: 1,
    transform: 'translateY(-50%) translateX(0) scale(1)',
    transitionDelay: `${TOOLTIP_HOVER_DELAY_MS}ms`,
  },
  tooltipVisibleNoDelay: {
    opacity: 1,
    transform: 'translateY(-50%) translateX(0) scale(1)',
    transitionDelay: '0ms',
  },
  /** Inline keyboard chip inside the tooltip. */
  kbd: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightMedium,
    backgroundColor: tokens.colorNeutralBackgroundInvertedDisabled,
    color: tokens.colorNeutralForegroundInverted,
    padding: `2px ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  /** Separator between the two label parts. */
  sep: {
    opacity: 0.5,
    marginLeft: tokens.spacingHorizontalXXS,
    marginRight: tokens.spacingHorizontalXXS,
  },
  /** Screen-reader-only description element referenced by aria-describedby. */
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
});
